import { toNestErrors, validateFieldsNatively } from '@hookform/resolvers'
import { merge } from '@mntn-dev/utilities'
import type { TFunction } from 'i18next'
import type {
  FieldErrors,
  FieldValues,
  Resolver as HookFormResolver,
} from 'react-hook-form'
import type { z } from 'zod'
import { isZodError } from '../utils/is-zod-error.ts'
import { parseErrorSchema } from '../utils/parse-error-schema.ts'
import {
  type FormFieldData,
  defaultRuleMapFactory,
} from '../validation/index.ts'
import type { ZodResolverOptions } from './types.ts'

export function zodInternalResolver<
  TFieldValues extends FieldValues,
  TContext,
  TSchema extends z.ZodSchema,
  TResolvedValues extends FieldValues = z.infer<TSchema>,
>(
  schema: TSchema,
  tFunction: TFunction<'validation'>,
  schemaOptions?: Partial<z.ParseParams>,
  resolverOptions?: ZodResolverOptions<TResolvedValues>
) {
  return (async (values, context, options) => {
    const errorMap = schemaOptions?.errorMap ?? defaultRuleMapFactory(tFunction)
    const input = merge(values, { context })

    try {
      const data = await schema[
        resolverOptions?.mode === 'async' ? 'parseAsync' : 'parse'
      ](input, {
        ...schemaOptions,
        errorMap: (issue, ctx) => {
          return errorMap(issue, {
            ...ctx,
            data: {
              ...(ctx.data ?? {}),
              fields: options.names?.reduce<
                Record<keyof TFieldValues, FormFieldData>
              >(
                (fields, fieldName) => {
                  // add labels and form data to context being passed to the resolver
                  const formData = options.fields[fieldName]
                  fields[fieldName] = {
                    ...formData,
                    label:
                      resolverOptions?.fieldLabelConfig?.getFieldLabel(
                        fieldName
                      ) ?? 'Field',
                  }

                  return fields
                },
                {} as Record<keyof TFieldValues, FormFieldData>
              ),
            },
          })
        },
      })

      options.shouldUseNativeValidation && validateFieldsNatively({}, options)

      return {
        errors: {} as FieldErrors<TFieldValues>,
        values: data as TResolvedValues,
      }
    } catch (error) {
      if (isZodError(error)) {
        const parsedErrors = parseErrorSchema(
          error.errors,
          !options.shouldUseNativeValidation && options.criteriaMode === 'all'
        )

        const errors = toNestErrors(parsedErrors, options)

        return {
          values: {},
          errors,
        }
      }

      throw error
    }
  }) as HookFormResolver<TFieldValues, TContext>
}

export function zodResolver<
  TSchema extends z.ZodSchema,
  TResolvedValues extends FieldValues = z.infer<TSchema>,
>(
  schema: TSchema,
  tFunction: TFunction<'validation'>,
  schemaOptions?: Partial<z.ParseParams>,
  resolverOptions?: ZodResolverOptions<TResolvedValues>
) {
  return { schema, tFunction, schemaOptions, resolverOptions }
}
